// GENERATED — DO NOT EDIT. Rendered by functions/test/v2/contract/renderClient.ts from functions/src/v2/contract/schema.ts.
// Regenerate: cd functions && UPDATE_V2_CLIENT=1 npx vitest run test/v2/contract.test.ts
// functions/test/v2/contract.test.ts fails when this file differs from a fresh render by a single byte.
//
// A dependency-free, fetch-based client for PROppeal's v2 contract. It imports nothing. Every method:
//   • POSTs JSON with `Authorization: Bearer <ID token>`, `X-Firebase-AppCheck: <App Check token>` and
//     `x-proppeal-contract: <CONTRACT_VERSION>`, taking both tokens from the providers passed to createV2Client;
//   • never throws: a non-2xx status, a network failure, a token provider or URL function that throws, and an
//     unreadable body each come back as a `{ ok: false }` value;
//   • reads the response's contract stamp, and returns `contractMismatch` only when the server's version cannot be
//     parsed, has a different MAJOR or an older MINOR than CONTRACT_VERSION, or equals CONTRACT_VERSION with a
//     different schemaSha256 (the schema changed without a bump). A newer MINOR only adds, so its answer is read.
// Bodies are typed, not validated: past the contract stamp, a response is trusted to be the shape its type names.
// A type ending in `(string & {})` is an OPEN set: it lists the values this file knows, and a newer server may send one
// it does not. Adding a value is a MINOR change, so code written against this file handles a value it does not list.
// The error code is open: a client handles a code it does not know by the error's `status`.

export const CONTRACT_VERSION = "2.33.0";
export const SCHEMA_SHA256 = "c1f6f9b75d7276a19a3c144f7812a2a2eb12db65ac282ffe82d8873ab47e49bc";

/** Whole dollars, tagged with the basis they are denominated in. */
export type Money<B extends string> = { basis: B; amount: number };

// ── Named types ─────────────────────────────────────────────────────────────────────────────────────────────────

export type Copy = {
  code: string;
  text: string;
};

export type PinnedCopy = {
  code: string;
  text: string;
  version: number;
  sha256: string;
};

export type AttestationCopy = {
  kind: "canRaiseAck" | "adoptAsOwn";
  variantId: string;
  code: string;
  text: string;
  version: number;
  sha256: string;
};

export type LegalDocRef = {
  kind: "terms" | "privacy";
  version: number;
  effectiveDate: string;
  url: string;
  sha256: string;
};

export type GuardId = "G-1" | "G-2" | "G-3" | "G-4" | "G-5" | "G-6" | "I-1/I-3" | "I-2" | "I-4/I-5" | "P-1" | "P-3" | "M-1" | "M-2" | "R-3" | "T-1" | "C-1" | "V-1" | "V-4" | "V-5" | "V-6" | "NEW-1" | "NEW-2" | "NEW-3" | "NEW-4" | "NEW-6" | "NEW-7" | "NEW-8" | "NEW-10" | "NEW-11" | "NEW-12" | "NEW-13" | "NEW-14" | "NEW-15" | "NEW-16" | "NEW-17" | "NEW-18" | "NEW-19" | "NEW-20" | "NEW-21" | "A-caseLooksWeak" | "A-hasExplanatoryGround" | "A-compsLead" | "A-portalCap";

export type EndpointName = "v2ResolveProperty" | "v2SearchComps" | "v2EvaluateCase" | "v2Value" | "v2GenerateLetter" | "v2Credits" | "v2Outcomes" | "v2CreateCheckout" | "v2EmailLetter";

export type DecisionNotRun = {
  why: "missingInput" | "notApplicable" | "notYetComputed" | "failed";
  missing?: Array<string>;
  computedBy?: EndpointName;
};

export type ContractStamp = {
  version: string;
  schemaSha256: string;
};

export type DegradedField = {
  field: string;
  problem: string;
};

export type V2ErrorCode = "BAD_REQUEST" | "UNAUTHENTICATED" | "APP_CHECK_FAILED" | "V2_DISABLED" | "CONTRACT_VERSION_UNSUPPORTED" | "NOT_FOUND" | "NOT_ENTITLED" | "APPEAL_LOCKED" | "SPEND_REFUSED" | "NO_CREDITS" | "RATE_LIMITED" | "LOOKUP_LIMIT" | "APPEAL_LIMIT" | "GENERATION_CAP" | "REVISION_CAP" | "EVALUATION_CAP" | "SEARCH_CAP" | "ATTESTATION_REQUIRED" | "LARGE_GAP_UNCONFIRMED" | "QUEUE_FAILED" | "PROVIDERS_UNAVAILABLE" | "REQUESTED_VALUE_WOULD_RAISE" | "NOTHING_TO_REVISE" | "INTERNAL" | "UNAVAILABLE" | "ACCOUNT_REQUIRED" | "EMAIL_NOT_VERIFIED" | "EMAIL_REJECTED" | "EMAIL_TOO_LARGE" | "CONSENT_REQUIRED" | (string & {});

export type V2Error = {
  contract: ContractStamp;
  requestId: string;
  error: {
    status: 400 | 401 | 402 | 403 | 404 | 409 | 422 | 426 | 429 | 500 | 503;
    code: V2ErrorCode;
    copy: Copy;
    decisions?: { [K in GuardId]?: 
      | {
        ran: true;
        outcome: "pass";
        value: unknown;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: unknown;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: unknown;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: unknown;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun };
  };
};

export type AppealStatus = "resolving" | "resolved" | "refused" | "spent" | "building" | "queued" | "generating" | "shipped" | "failed" | "filed" | "decided";

export type LetterStatus = "queued" | "generating" | "shipped" | "failed";

export type PerAppealCaps = {
  generations: {
    used: number;
    max: 8;
  };
  revisions: {
    used: number;
    max: 20;
  };
  evaluations: {
    used: number;
    max: 10;
  };
  providerSearches: {
    used: number;
    max: 1;
  };
};

export type PropertyTypeCode = "Single-family" | "Twin/Semi-detached" | "Townhouse" | "Condo" | "Manufactured" | "Multi-unit" | "Other";

export type PropertyRefusal = "manufactured" | "multiUnit" | "unclassified";

export type OutcomeResult = "filed" | "won" | "partiallyWon" | "denied" | "raised" | "withdrawn";

export type UnitCandidate = {
  candidateId: string;
  unitNumber: string | null;
  parcelId: string | null;
  assessedValue: Money<"assessed"> | null;
  sqft: number | null;
  beds: number | null;
  baths: number | null;
  yearBuilt: number | null;
  lastSalePrice: Money<"market"> | null;
  lastSaleDate: string | null;
};

export type RecordStanding = {
  standing: "adopted" | "awaitingOwner" | "notAdopted";
  reason: "noRecord" | "differentProperty" | "wholeBuilding" | "ownerDeclined" | "recordsUnavailable" | null;
  question: "nearMiss" | "unit" | null;
  recordAddress: string | null;
  unitCandidates: Array<UnitCandidate>;
  unitCandidatesTruncated: boolean;
  confirmed: boolean | null;
  unitChoice: string | null;
  withoutLookup: boolean;
};

export type SubjectView = {
  address: string;
  unit: string | null;
  state: string | null;
  county: string | null;
  parcel: {
    value: string;
    source: "provider" | "ownerEntered" | "ownerCorrected" | "county";
    provider?: "realie" | "rentcast" | "attom" | "ccao";
    retrievedAt: string;
    recordValue?: string | null;
  } | null;
  propertyType: {
    value: PropertyTypeCode;
    source: "provider" | "ownerEntered" | "ownerCorrected" | "county";
    provider?: "realie" | "rentcast" | "attom" | "ccao";
    retrievedAt: string;
    recordValue?: PropertyTypeCode | null;
  } | null;
  assessedValue: {
    value: Money<"assessed">;
    source: "provider" | "ownerEntered" | "ownerCorrected" | "county";
    provider?: "realie" | "rentcast" | "attom" | "ccao";
    retrievedAt: string;
    recordValue?: Money<"assessed"> | null;
  } | null;
  marketValue: {
    value: Money<"market">;
    source: "provider" | "ownerEntered" | "ownerCorrected" | "county";
    provider?: "realie" | "rentcast" | "attom" | "ccao";
    retrievedAt: string;
    recordValue?: Money<"market"> | null;
  } | null;
  impliedMarketValue: Money<"market"> | null;
  sqft: {
    value: number;
    source: "provider" | "ownerEntered" | "ownerCorrected" | "county";
    provider?: "realie" | "rentcast" | "attom" | "ccao";
    retrievedAt: string;
    recordValue?: number | null;
  } | null;
  beds: {
    value: number;
    source: "provider" | "ownerEntered" | "ownerCorrected" | "county";
    provider?: "realie" | "rentcast" | "attom" | "ccao";
    retrievedAt: string;
    recordValue?: number | null;
  } | null;
  baths: {
    value: number;
    source: "provider" | "ownerEntered" | "ownerCorrected" | "county";
    provider?: "realie" | "rentcast" | "attom" | "ccao";
    retrievedAt: string;
    recordValue?: number | null;
  } | null;
  yearBuilt: {
    value: number;
    source: "provider" | "ownerEntered" | "ownerCorrected" | "county";
    provider?: "realie" | "rentcast" | "attom" | "ccao";
    retrievedAt: string;
    recordValue?: number | null;
  } | null;
  lastSale: {
    price: Money<"market">;
    date: string | null;
    distressed: boolean | null;
  } | null;
  lotSizeSqft?: {
    value: number;
    source: "provider" | "ownerEntered" | "ownerCorrected" | "county";
    provider?: "realie" | "rentcast" | "attom" | "ccao";
    retrievedAt: string;
    recordValue?: number | null;
  } | null;
  assessedYear?: {
    value: number;
    source: "provider" | "ownerEntered" | "ownerCorrected" | "county";
    provider?: "realie" | "rentcast" | "attom" | "ccao";
    retrievedAt: string;
    recordValue?: number | null;
  } | null;
  coordinates?: {
    latitude: number;
    longitude: number;
  } | null;
  record?: RecordStanding;
  corrections?: {
    sqft?: number;
    beds?: number;
    baths?: number;
    assessedValue?: Money<"assessed">;
    marketValue?: Money<"market">;
    parcel?: string;
    propertyType?: PropertyTypeCode;
    yearBuilt?: number;
    lotSizeSqft?: number;
    lastSalePrice?: Money<"market">;
    lastSaleDate?: string;
    lastSaleDistressed?: true;
    ratioPercent?: number;
    taxYear?: number;
    county?: string;
    state?: string;
    appealDeadline?: string;
  };
  taxYear?: number;
  appealDeadline?: string | null;
  ratioCheck?: {
    impliedMarket: Money<"market">;
    onFile: Money<"market">;
  } | null;
  sqftWording?: {
    recordLabel: "countyRecord" | "publicRecords";
    note: "none" | "basementMatch" | "basementCaption";
  };
};

export type AppealSnapshot = {
  appealId: string;
  status: AppealStatus;
  createdAt: string;
  updatedAt: string;
  entitlement: {
    unlocked: boolean;
    spentAt: string | null;
    caps: PerAppealCaps;
  };
  subject: SubjectView | null;
  requestedValue: Money<"market"> | null;
  letter: {
    latestGenerationAt: number | null;
    status: LetterStatus | null;
  };
  outcome: {
    result: OutcomeResult;
    recordedAt: string;
    finalAssessedValue: Money<"assessed"> | null;
    note: string | null;
    sharedAnonymously: boolean | null;
  } | null;
};

export type PurchaseVsImplied = {
  purchase: Money<"market">;
  impliedMarket: Money<"market">;
};

export type SourcedDate =
  | {
    authority: "statutory";
    date: string;
    citation: string;
  }
  | {
    authority: "county";
    date: string;
    verification: {
      source: string;
      verifiedOn: string;
    };
  };

export type ProtestWindow =
  | {
    standing: "inside";
    closes: SourcedDate;
    filedWith: string;
    notice: Copy;
  }
  | {
    standing: "outside";
    nextOpens: SourcedDate;
    filedWith: string;
    notice: Copy;
    earlier: {
      label: Copy;
      opens: SourcedDate;
      closes: SourcedDate;
      appliesWhen: Copy;
    } | null;
  }
  | {
    standing: "unknown";
  };

export type FilingDeadline =
  | {
    kind: "fixedStatutory";
    date: SourcedDate;
    label: Copy;
    notice: Copy;
  }
  | {
    kind: "noticeDrivenFloor";
    floor: SourcedDate;
    label: Copy;
    notice: Copy;
  }
  | {
    kind: "noticeDrivenBackstop";
    alarm: string;
    label: Copy;
    notice: Copy;
  }
  | {
    kind: "countyVariant";
    resolved:
      | {
        at: "county";
        date: SourcedDate;
      }
      | {
        at: "stateFloor";
        floor: SourcedDate;
      }
      | {
        at: "stateBackstop";
        alarm: string;
      };
    label: Copy;
    notice: Copy;
  }
  | {
    kind: "unverified";
    alarm: string;
  };

export type GeocodeMatch = {
  geocoder: string;
  precision: "rooftop" | "rangeInterpolated" | "geometricCenter" | "approximate" | "unknown";
};

export type AddressSurvivors = {
  lot: string | null;
  unit: string | null;
  directional: string | null;
};

export type AddressUsed = "typed" | "resolved";

export type LiftedUnit = {
  value: string;
  source: "ownerField" | "addressLine";
  notice: Copy | null;
};

export type AddressResolution =
  | {
    resolvedBy: "geocoder";
    typed: string;
    resolved: string;
    used: AddressUsed;
    survivors: AddressSurvivors;
    unit: LiftedUnit | null;
    geocode: GeocodeMatch;
  }
  | {
    resolvedBy: "providerRecord";
    typed: string;
    resolved: string | null;
    used: AddressUsed;
    survivors: AddressSurvivors;
    unit: LiftedUnit | null;
  }
  | {
    resolvedBy: "ownerEntered";
    typed: string;
    resolved: string | null;
    used: AddressUsed;
    survivors: AddressSurvivors;
    unit: LiftedUnit | null;
  };

export type Ratio = {
  value: number;
  isFractional: boolean;
  origin: "published" | "restated" | "genuineFullValue" | "statutoryBandOverride" | "ownerEntered" | "assumedFullValue";
  confidence: string;
  basis: Copy;
  effectiveFrom: string | null;
  effectiveTo: string | null;
};

export type CanRaise = {
  risk: "cannotRaiseAtBoard" | "canRaise" | "deNovoRisk" | "unknown";
  requiresAcknowledgment: boolean;
  card: Copy | null;
  noCaseSentence: Copy | null;
};

export type CapRegime = {
  kind: "none" | "prop13" | "homesteadCapTX" | "saveOurHomesFL" | "proposalAMI" | "otherCap" | "measure50";
  isAcquisitionValue: boolean;
  recognized: boolean;
  copy: Copy | null;
};

export type Jurisdiction = {
  state: string | null;
  county: string | null;
  fips: string | null;
  addressee: string | null;
  filingTerm: "appeal" | "protest" | "petition";
  deadline: FilingDeadline | null;
  protestWindow: ProtestWindow;
};

export type SpendGate = {
  allowed: boolean;
  blockedBy: Array<"G-1" | "G-5" | "G-4" | "G-3">;
  requiresConfirmation: Array<"address">;
};

export type ResolutionBody = {
  address: AddressResolution;
  jurisdiction: Jurisdiction;
  ratio: Ratio;
  canRaise: CanRaise;
  capRegime: CapRegime;
  spend: SpendGate;
  subject: SubjectView | null;
};

export type ResolvePropertyRequest =
  | {
    op: "address";
    appealId?: string;
    typedAddress: string;
    unit?: string | null;
    propertyType?: PropertyTypeCode | null;
    taxYear?: number;
    confirmations?: {
      address?: boolean;
    };
  }
  | {
    op: "record";
    appealId: string;
    ownerName?: string;
    freshLookup?: boolean;
    corrections?: {
      sqft?: number;
      beds?: number;
      baths?: number;
      assessedValue?: Money<"assessed">;
      marketValue?: Money<"market">;
      parcel?: string;
      propertyType?: PropertyTypeCode;
      yearBuilt?: number;
      lotSizeSqft?: number;
      lastSalePrice?: Money<"market">;
      lastSaleDate?: string;
      lastSaleDistressed?: true;
      ratioPercent?: number;
      taxYear?: number;
      county?: string;
      state?: string;
      appealDeadline?: string;
    };
    confirmations?: {
      record?: boolean;
    };
    unitChoice?: string;
    withoutLookup?: true;
  }
  | {
    op: "purchaseNotice";
    appealId: string;
  }
  | {
    op: "resume";
    appealId: string;
  }
  | {
    op: "freeCheck";
    appealId: string;
  };

export type ResolvePropertyResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: {
    "G-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          state: string | null;
          supported: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          state: string | null;
          supported: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          state: string | null;
          supported: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          state: string | null;
          supported: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "G-2":
      | {
        ran: true;
        outcome: "pass";
        value: CanRaise;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: CanRaise;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: CanRaise;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: CanRaise;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "G-3":
      | {
        ran: true;
        outcome: "pass";
        value: {
          confirmationRequired: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          confirmationRequired: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          confirmationRequired: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          confirmationRequired: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "G-4":
      | {
        ran: true;
        outcome: "pass";
        value: {
          unitRequired: boolean;
          unitMissing: boolean;
          minimallyComplete: boolean | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          unitRequired: boolean;
          unitMissing: boolean;
          minimallyComplete: boolean | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          unitRequired: boolean;
          unitMissing: boolean;
          minimallyComplete: boolean | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          unitRequired: boolean;
          unitMissing: boolean;
          minimallyComplete: boolean | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "G-5":
      | {
        ran: true;
        outcome: "pass";
        value: {
          refusal: PropertyRefusal | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          refusal: PropertyRefusal | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          refusal: PropertyRefusal | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          refusal: PropertyRefusal | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "I-1/I-3":
      | {
        ran: true;
        outcome: "pass";
        value: AddressResolution;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: AddressResolution;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: AddressResolution;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: AddressResolution;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "I-2":
      | {
        ran: true;
        outcome: "pass";
        value: {
          mismatch: "houseNumber" | "street" | "directional" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          mismatch: "houseNumber" | "street" | "directional" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          mismatch: "houseNumber" | "street" | "directional" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          mismatch: "houseNumber" | "street" | "directional" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "I-4/I-5":
      | {
        ran: true;
        outcome: "pass";
        value: {
          unitMismatch: boolean;
          designator: string | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          unitMismatch: boolean;
          designator: string | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          unitMismatch: boolean;
          designator: string | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          unitMismatch: boolean;
          designator: string | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "M-2":
      | {
        ran: true;
        outcome: "pass";
        value: CapRegime;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: CapRegime;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: CapRegime;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: CapRegime;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "T-1":
      | {
        ran: true;
        outcome: "pass";
        value: ProtestWindow;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: ProtestWindow;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: ProtestWindow;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: ProtestWindow;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "V-5":
      | {
        ran: true;
        outcome: "pass";
        value: Ratio;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: Ratio;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: Ratio;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: Ratio;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "V-6":
      | {
        ran: true;
        outcome: "pass";
        value: {
          isFractional: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          isFractional: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          isFractional: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          isFractional: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "P-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          impliedMarket: Money<"market"> | null;
          diagnosis?: {
            banner: "delaware" | "vintage" | "taxBill" | "fractional" | "general";
            alert: "taxBill" | "vintage" | "general";
          };
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          impliedMarket: Money<"market"> | null;
          diagnosis?: {
            banner: "delaware" | "vintage" | "taxBill" | "fractional" | "general";
            alert: "taxBill" | "vintage" | "general";
          };
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          impliedMarket: Money<"market"> | null;
          diagnosis?: {
            banner: "delaware" | "vintage" | "taxBill" | "fractional" | "general";
            alert: "taxBill" | "vintage" | "general";
          };
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          impliedMarket: Money<"market"> | null;
          diagnosis?: {
            banner: "delaware" | "vintage" | "taxBill" | "fractional" | "general";
            alert: "taxBill" | "vintage" | "general";
          };
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          sqft: number | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          sqft: number | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          sqft: number | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          sqft: number | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-2":
      | {
        ran: true;
        outcome: "pass";
        value: {
          suspect: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          suspect: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          suspect: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          suspect: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-3":
      | {
        ran: true;
        outcome: "pass";
        value: {
          sqftMissing: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          sqftMissing: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          sqftMissing: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          sqftMissing: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-7":
      | {
        ran: true;
        outcome: "pass";
        value: PurchaseVsImplied | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: PurchaseVsImplied | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: PurchaseVsImplied | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: PurchaseVsImplied | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
  };
  degraded: Array<DegradedField>;
  body: ResolutionBody;
};

export type PurchaseNotice = {
  purchase: Money<"market">;
  impliedMarket: Money<"market">;
  saleDate: string | null;
  state: string;
  risk: "cannotRaiseAtBoard" | "canRaise" | "deNovoRisk" | "unknown";
};

export type ResolvePurchaseNoticeResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    purchaseNotice: PurchaseNotice | null;
  };
};

export type FreeCheckLine = {
  id: string;
  text: string;
  href?: string;
};

export type FreeCheckExtras = {
  deadline?: {
    dueDate: string;
    daysLeft: number;
    lines: Array<FreeCheckLine>;
    calendar: {
      title: string;
      date: string;
    };
  };
  rollChange?: {
    countyPct: number;
    year: number;
    basis: "existingHomesExclGrowth" | "medianHome" | (string & {});
    ownPct?: number;
    lines: Array<FreeCheckLine>;
    sourceLine: FreeCheckLine;
    source: {
      name: string;
      url: string;
    };
  };
  taxCost?: {
    rate: number;
    rateBasis: "parcel" | "countyAverage" | (string & {});
    annualTax: number | null;
    per10k: number;
    lines: Array<FreeCheckLine>;
    source: {
      name: string;
      url: string;
      year: number;
    };
  };
  exemptions?: Array<{
    key: string;
    name: string;
    status: "notOnRecord" | "onRecord" | "unknown" | (string & {});
    annualValue?: number;
    applyUrl: string;
    lines: Array<FreeCheckLine>;
  }>;
  priceTrend?: {
    changePct: number;
    sinceYear: number;
    series: Array<number>;
    lines: Array<FreeCheckLine>;
    source: {
      name: string;
      url: string;
    };
  };
  homeValues?: {
    median: number;
    moe: number;
    geography: "tract" | (string & {});
    geoid: string;
    surveyYears: Array<number>;
    above: boolean;
    lines: Array<FreeCheckLine>;
    sourceLine: FreeCheckLine;
    source: {
      name: string;
      url: string;
    };
  };
  appealRate?: {
    sharePct: number;
    year: number;
    lines: Array<FreeCheckLine>;
    source: {
      name: string;
      url: string;
    };
  };
  estimator: {
    assessedValue: number | null;
    taxRate: number | null;
    ratio: number | null;
    exemptionAmount?: number;
    templates: {
      saving: string;
      explain: string;
      flagged: string;
      flaggedOne: string;
      noSaving: string;
      disclaimer: string;
      noticeLabel: string;
      noticeHelp: string;
      worthLabel: string;
      wrongLabel: string;
      wrongItems: Array<string>;
    };
  };
  close: {
    kind: "lockedPreview" | (string & {});
    lines: Array<FreeCheckLine>;
  };
  labels: {
    deadline: string;
    addToCalendar: string;
    taxCost: string;
    exemptions: string;
    notOnRecord: string;
    howToApply: string;
    priceTrend: string;
    appealRate: string;
    estimator: string;
    notice: string;
    homeValues?: string;
    rollChange?: string;
  };
};

export type FreeCheckScreen = {
  lines: Array<FreeCheckLine>;
  extras: FreeCheckExtras | null;
};

export type FreeCheckFirstLook = {
  caption: FreeCheckLine | null;
  rows: Array<{
    kind: "value" | "history" | "lastSale" | "deadline" | (string & {});
    lines: Array<FreeCheckLine>;
  }>;
};

export type FreeCheckUnavailableReason = "FREE_CHECK_ALREADY_USED" | "DAILY_GLOBAL_CEILING_REACHED" | "IDENTITY_DAILY_LIMIT_REACHED" | "GLOBAL_BREAKER_TRIPPED" | "COUNTY_SWITCHED_OFF" | "FEATURE_OFF" | "NO_CLIENT_IDENTITY" | (string & {});

export type ResolveFreeCheckResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    screen: FreeCheckScreen | null;
    firstLook: FreeCheckFirstLook | null;
    unavailableReason: FreeCheckUnavailableReason | null;
    servedFromCache: boolean;
  };
};

export type CompView = {
  compId: string;
  address: string;
  salePrice: Money<"market"> | null;
  saleDate: string | null;
  sqft: number | null;
  beds: number | null;
  baths: number | null;
  distanceMi: number | null;
  propertyType: string | null;
  distressed: boolean | null;
  source: "provider" | "owner";
  included: boolean;
  usable: boolean;
  keptDespiteOutlier: boolean;
  unitCount?: number | null;
  yearBuilt?: number | null;
};

export type SearchCompsRequest =
  | {
    op: "search";
    appealId: string;
    radiusMiles?: number;
  }
  | {
    op: "edit";
    appealId: string;
    include?: Array<string>;
    exclude?: Array<string>;
    keepDespiteOutlier?: Array<string>;
    ownerComps?: Array<{
      address: string;
      salePrice: Money<"market">;
      saleDate?: string;
      sqft?: number;
      beds?: number;
      baths?: number;
      distanceMi?: number;
      unitCount?: number;
      yearBuilt?: number;
    }>;
    updates?: Array<{
      compId: string;
      address?: string;
      salePrice?: Money<"market"> | null;
      saleDate?: string | null;
      sqft?: number | null;
      beds?: number | null;
      baths?: number | null;
      distanceMi?: number | null;
      unitCount?: number | null;
      yearBuilt?: number | null;
    }>;
    remove?: Array<string>;
  }
  | {
    op: "lookup";
    appealId: string;
    address: string;
  };

export type SearchCompsResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: {
    "C-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-21":
      | {
        ran: true;
        outcome: "pass";
        value: {
          usableCount: number;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          usableCount: number;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          usableCount: number;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          usableCount: number;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
  };
  degraded: Array<DegradedField>;
  body: {
    comps: Array<CompView>;
    avm: {
      estimate: Money<"market">;
      low: Money<"market"> | null;
      high: Money<"market"> | null;
      source: string | null;
    } | null;
    meta: {
      candidatesConsidered: number;
      typeFilteredOut: number;
      providerError: boolean;
      dataGap: boolean;
      poolSize: number;
      ranked: number;
    };
  };
};

export type CompLookupResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: {
    "C-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
  };
  degraded: Array<DegradedField>;
  body: {
    lookup:
      | {
        outcome: "filled";
        sqft: number | null;
        beds: number | null;
        baths: number | null;
        yearBuilt: number | null;
        distanceMi: number | null;
        copy: Copy;
      }
      | {
        outcome: "declined";
        reason: "noRecord" | "differentProperty";
        copy: Copy;
      };
  };
};

export type EvaluateCaseRequest = {
  appealId: string;
  narrative?: boolean;
  narrativeOnFile?: boolean;
};

export type EvaluateCaseResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: {
    "C-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "A-caseLooksWeak":
      | {
        ran: true;
        outcome: "pass";
        value: {
          fork: "none" | "thinData" | "noCase";
          supportingComps: number;
          nearbySales: number;
          independentGrounds: Array<"condition" | "recordError" | "photos" | "notes" | "uniformity">;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          fork: "none" | "thinData" | "noCase";
          supportingComps: number;
          nearbySales: number;
          independentGrounds: Array<"condition" | "recordError" | "photos" | "notes" | "uniformity">;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          fork: "none" | "thinData" | "noCase";
          supportingComps: number;
          nearbySales: number;
          independentGrounds: Array<"condition" | "recordError" | "photos" | "notes" | "uniformity">;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          fork: "none" | "thinData" | "noCase";
          supportingComps: number;
          nearbySales: number;
          independentGrounds: Array<"condition" | "recordError" | "photos" | "notes" | "uniformity">;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "R-3":
      | {
        ran: true;
        outcome: "pass";
        value: {
          shape: "belowMarket" | "recentPurchase";
          assessedBasis: Money<"market">;
          compCount: number;
          adjustedMidpoint: Money<"market">;
          ownPurchase: Money<"market"> | null;
          ownPurchaseWhen: string | null;
          headline: Copy;
          canRaiseSentence: Copy | null;
          savesYou: Copy;
          whatWouldChangeThis: Array<Copy>;
          gapAbove: Money<"market">;
          percentAbove: number;
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          shape: "belowMarket" | "recentPurchase";
          assessedBasis: Money<"market">;
          compCount: number;
          adjustedMidpoint: Money<"market">;
          ownPurchase: Money<"market"> | null;
          ownPurchaseWhen: string | null;
          headline: Copy;
          canRaiseSentence: Copy | null;
          savesYou: Copy;
          whatWouldChangeThis: Array<Copy>;
          gapAbove: Money<"market">;
          percentAbove: number;
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          shape: "belowMarket" | "recentPurchase";
          assessedBasis: Money<"market">;
          compCount: number;
          adjustedMidpoint: Money<"market">;
          ownPurchase: Money<"market"> | null;
          ownPurchaseWhen: string | null;
          headline: Copy;
          canRaiseSentence: Copy | null;
          savesYou: Copy;
          whatWouldChangeThis: Array<Copy>;
          gapAbove: Money<"market">;
          percentAbove: number;
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          shape: "belowMarket" | "recentPurchase";
          assessedBasis: Money<"market">;
          compCount: number;
          adjustedMidpoint: Money<"market">;
          ownPurchase: Money<"market"> | null;
          ownPurchaseWhen: string | null;
          headline: Copy;
          canRaiseSentence: Copy | null;
          savesYou: Copy;
          whatWouldChangeThis: Array<Copy>;
          gapAbove: Money<"market">;
          percentAbove: number;
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-11":
      | {
        ran: true;
        outcome: "pass";
        value: {
          verdictBefore: string | null;
          verdictAfter: string | null;
          rule: "compSupport" | "acquisitionValue" | "lowConfidence" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          verdictBefore: string | null;
          verdictAfter: string | null;
          rule: "compSupport" | "acquisitionValue" | "lowConfidence" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          verdictBefore: string | null;
          verdictAfter: string | null;
          rule: "compSupport" | "acquisitionValue" | "lowConfidence" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          verdictBefore: string | null;
          verdictAfter: string | null;
          rule: "compSupport" | "acquisitionValue" | "lowConfidence" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-12":
      | {
        ran: true;
        outcome: "pass";
        value: {
          filled: 0 | 1 | 2 | 3 | 4 | 5;
          label: Copy;
          citedCount: number;
          restingOn: Array<"comps" | "condition" | "uniformity" | "recordError">;
          detail: Copy;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          filled: 0 | 1 | 2 | 3 | 4 | 5;
          label: Copy;
          citedCount: number;
          restingOn: Array<"comps" | "condition" | "uniformity" | "recordError">;
          detail: Copy;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          filled: 0 | 1 | 2 | 3 | 4 | 5;
          label: Copy;
          citedCount: number;
          restingOn: Array<"comps" | "condition" | "uniformity" | "recordError">;
          detail: Copy;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          filled: 0 | 1 | 2 | 3 | 4 | 5;
          label: Copy;
          citedCount: number;
          restingOn: Array<"comps" | "condition" | "uniformity" | "recordError">;
          detail: Copy;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "A-compsLead":
      | {
        ran: true;
        outcome: "pass";
        value: {
          compsLead: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          compsLead: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          compsLead: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          compsLead: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-19":
      | {
        ran: true;
        outcome: "pass";
        value: {
          uniformityAvailable: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          uniformityAvailable: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          uniformityAvailable: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          uniformityAvailable: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "A-hasExplanatoryGround":
      | {
        ran: true;
        outcome: "pass";
        value: {
          excused: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          excused: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          excused: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          excused: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
  };
  degraded: Array<DegradedField>;
  body: {
    narrative: {
      headline: string;
      explanationPoints: Array<string>;
      source: "v1.evaluateCase";
      verdict: "strongCase" | "possibleCase" | "borderline" | "inconclusive" | "insufficientData" | "noCaseAcquisitionValue" | "noCaseAvmCorroborated" | null;
      caveats: string;
      strongestCompIds: Array<string>;
      strongest: {
        count: number;
        sqftLow: number | null;
        sqftHigh: number | null;
        priceLow: Money<"market"> | null;
        priceHigh: Money<"market"> | null;
      } | null;
      stale: boolean;
    } | null;
    narrativeReason: "notRequested" | "modelUnavailable" | "evaluationCap" | "rateLimited" | "modelFailed" | "noProse" | null;
    numberLine: {
      assessed: Money<"market"> | null;
      fractional: boolean;
      low: Money<"market">;
      high: Money<"market">;
      gap: {
        midpoint: Money<"market">;
        amount: Money<"market">;
        percent: number;
      } | null;
    } | null;
    caseQuality: {
      precision: "high" | "medium" | "low";
      blurb: Copy;
      caveat: Copy | null;
    } | null;
    uniformity: {
      regime: "median_assessed" | "ratio_corridor";
      supportsRelief: boolean;
      backfireRisk: boolean;
      headline: string;
      note: string | null;
      compCount: number;
      subjectRatio: number | null;
      corridorLow: number | null;
      corridorHigh: number | null;
      ratioImplausible: boolean;
      contestedBy: {
        estimate: Money<"market">;
        low: Money<"market"> | null;
        high: Money<"market"> | null;
      } | null;
      equityValue: Money<"assessed"> | null;
      equityMarket: Money<"market"> | null;
      adoptedRequest: Money<"market"> | null;
      lowersAsk: boolean;
      adopted: boolean;
    } | null;
    groundsSeed: Array<"overvaluation" | "comparableSales" | "conditionIssues" | "factualError">;
    strengthen: boolean;
  };
};

export type AskSource = "engine" | "owner" | "equity" | "income";

export type ValueRequest =
  | {
    op: "compute";
    appealId: string;
  }
  | {
    op: "setAsk";
    appealId: string;
    requestedValue: Money<"market"> | null;
    source: AskSource;
  }
  | {
    op: "evidence";
    appealId: string;
    reasons?: Array<"conditionIssues" | "factualError" | "uniformity" | "incomeApproach" | "overvaluation" | "comparableSales">;
    conditionNote?: string;
    purchaseContextNote?: string;
    recordErrorNote?: string;
    photos?: Array<{
      photoId?: string;
      base64?: string;
      contentType?: "image/jpeg" | "image/png";
      caption?: string;
      captionAuthor?: "owner" | "drafted";
    }>;
    largeGap?: {
      confirmed: boolean;
      reasons: Array<string>;
    };
    uniformity?: {
      adopt: boolean;
    };
  }
  | {
    op: "photo";
    appealId: string;
    photoId: string;
  };

export type ValueResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: {
    "C-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "V-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          suggestionAvailable: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          suggestionAvailable: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          suggestionAvailable: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          suggestionAvailable: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "V-4":
      | {
        ran: true;
        outcome: "pass";
        value: {
          posture: "reducesBill" | "wouldNotLowerBill" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          posture: "reducesBill" | "wouldNotLowerBill" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          posture: "reducesBill" | "wouldNotLowerBill" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          posture: "reducesBill" | "wouldNotLowerBill" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "G-6":
      | {
        ran: true;
        outcome: "pass";
        value: {
          blocked: boolean;
          requestedAssessed: Money<"assessed"> | null;
          currentAssessed: Money<"assessed"> | null;
          tier: "cannotRaiseAtBoard" | "canRaise" | "deNovoRisk" | "unknown";
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          blocked: boolean;
          requestedAssessed: Money<"assessed"> | null;
          currentAssessed: Money<"assessed"> | null;
          tier: "cannotRaiseAtBoard" | "canRaise" | "deNovoRisk" | "unknown";
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          blocked: boolean;
          requestedAssessed: Money<"assessed"> | null;
          currentAssessed: Money<"assessed"> | null;
          tier: "cannotRaiseAtBoard" | "canRaise" | "deNovoRisk" | "unknown";
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          blocked: boolean;
          requestedAssessed: Money<"assessed"> | null;
          currentAssessed: Money<"assessed"> | null;
          tier: "cannotRaiseAtBoard" | "canRaise" | "deNovoRisk" | "unknown";
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-4":
      | {
        ran: true;
        outcome: "pass";
        value: {
          annual: number | null;
          withheldBecause: "capState" | "unknownRate" | "notReduction" | "measure50" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          annual: number | null;
          withheldBecause: "capState" | "unknownRate" | "notReduction" | "measure50" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          annual: number | null;
          withheldBecause: "capState" | "unknownRate" | "notReduction" | "measure50" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          annual: number | null;
          withheldBecause: "capState" | "unknownRate" | "notReduction" | "measure50" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "M-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          currentYearSaving: number;
          blockedByCap: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          currentYearSaving: number;
          blockedByCap: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          currentYearSaving: number;
          blockedByCap: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          currentYearSaving: number;
          blockedByCap: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "P-3":
      | {
        ran: true;
        outcome: "pass";
        value: {
          issue: "notDollar" | "mavAboveRmv" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          issue: "notDollar" | "mavAboveRmv" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          issue: "notDollar" | "mavAboveRmv" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          issue: "notDollar" | "mavAboveRmv" | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-7":
      | {
        ran: true;
        outcome: "pass";
        value: PurchaseVsImplied | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: PurchaseVsImplied | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: PurchaseVsImplied | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: PurchaseVsImplied | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "A-hasExplanatoryGround":
      | {
        ran: true;
        outcome: "pass";
        value: {
          excused: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          excused: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          excused: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          excused: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-8":
      | {
        ran: true;
        outcome: "pass";
        value: {
          warn: {
            percentBelow: number;
            purchaseWhen: string | null;
          } | null;
          refuseAtGeneration: {
            purchase: Money<"market">;
          } | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          warn: {
            percentBelow: number;
            purchaseWhen: string | null;
          } | null;
          refuseAtGeneration: {
            purchase: Money<"market">;
          } | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          warn: {
            percentBelow: number;
            purchaseWhen: string | null;
          } | null;
          refuseAtGeneration: {
            purchase: Money<"market">;
          } | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          warn: {
            percentBelow: number;
            purchaseWhen: string | null;
          } | null;
          refuseAtGeneration: {
            purchase: Money<"market">;
          } | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-10":
      | {
        ran: true;
        outcome: "pass";
        value: {
          clampedFrom: Money<"market">;
          clampedTo: Money<"market">;
          by: "purchase" | "equity";
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          clampedFrom: Money<"market">;
          clampedTo: Money<"market">;
          by: "purchase" | "equity";
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          clampedFrom: Money<"market">;
          clampedTo: Money<"market">;
          by: "purchase" | "equity";
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          clampedFrom: Money<"market">;
          clampedTo: Money<"market">;
          by: "purchase" | "equity";
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-13":
      | {
        ran: true;
        outcome: "pass";
        value: {
          compsDivergeBelowAvmLow: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          compsDivergeBelowAvmLow: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          compsDivergeBelowAvmLow: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          compsDivergeBelowAvmLow: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-14":
      | {
        ran: true;
        outcome: "pass";
        value: {
          below: number;
          above: number;
          total: number;
          subjectPerSqft: number;
          majority: "mostlyBelow" | "roughlyEven" | "mostlyAtOrAbove";
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          below: number;
          above: number;
          total: number;
          subjectPerSqft: number;
          majority: "mostlyBelow" | "roughlyEven" | "mostlyAtOrAbove";
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          below: number;
          above: number;
          total: number;
          subjectPerSqft: number;
          majority: "mostlyBelow" | "roughlyEven" | "mostlyAtOrAbove";
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          below: number;
          above: number;
          total: number;
          subjectPerSqft: number;
          majority: "mostlyBelow" | "roughlyEven" | "mostlyAtOrAbove";
        } | null;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-15":
      | {
        ran: true;
        outcome: "pass";
        value: {
          isLargeGap: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          isLargeGap: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          isLargeGap: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          isLargeGap: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-16":
      | {
        ran: true;
        outcome: "pass";
        value: {
          marketOverrideApplied: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          marketOverrideApplied: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          marketOverrideApplied: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          marketOverrideApplied: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-18":
      | {
        ran: true;
        outcome: "pass";
        value: {
          midCycle: boolean | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          midCycle: boolean | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          midCycle: boolean | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          midCycle: boolean | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-20":
      | {
        ran: true;
        outcome: "pass";
        value: {
          capRateImplausible: boolean;
          incomeLeads: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          capRateImplausible: boolean;
          incomeLeads: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          capRateImplausible: boolean;
          incomeLeads: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          capRateImplausible: boolean;
          incomeLeads: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-21":
      | {
        ran: true;
        outcome: "pass";
        value: {
          hasGround: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          hasGround: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          hasGround: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          hasGround: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
  };
  degraded: Array<DegradedField>;
  body: {
    value: {
      impliedMarketValue: Money<"market"> | null;
      comparisonBasis: {
        kind: "market" | "acquisitionValue" | "unavailable";
        amount: Money<"market"> | null;
      };
      suggestion: {
        value: Money<"market">;
        indicatedValue: Money<"market">;
        low: Money<"market">;
        high: Money<"market">;
        confidence: "high" | "medium" | "low";
        compCount: number;
        supportsReduction: boolean;
        tempered: boolean;
        confidenceBlurb: Copy;
        marketValue: Money<"market">;
        ratePerSquareFoot: number | null;
        subjectSqft: number | null;
        weakEvidence: boolean;
        reachOnly: boolean;
        flooredAboveIndicators: boolean;
        submarketHeterogeneous: boolean;
        valueAssessed: Money<"assessed">;
        marketValueAssessed: Money<"assessed">;
      } | null;
      excludedOutliers: Array<{
        compId: string;
        adjustedValue: Money<"market">;
        medianReference: Money<"market">;
        reason: Copy;
        address: string;
      }>;
      citationZones: {
        supporting: Array<string>;
        bracketing: Array<string>;
      };
      filedCompIds: Array<string>;
      exhibitCompIds: Array<string>;
      filing: {
        compIds: Array<string>;
        upperRangeCompIds: Array<string>;
      };
      comps: Array<{
        compId: string;
        adjustedValue: Money<"market"> | null;
        stage: "raw" | "timeAdjusted" | "sizeAdjusted" | null;
        adjustmentsText: string;
        match: number | null;
        recency: {
          band: "recent" | "standard" | "older" | "dated";
          months: number;
          before: boolean;
        } | null;
        timeAdjusted: {
          price: Money<"market">;
          percent: number;
        } | null;
        timeAdjustmentGap: boolean;
        sizeLine: {
          kind: "trimmed" | "raised" | "same" | "notSizedTimeAdjusted" | "notSizedAsSold";
          adjusted: Money<"market">;
        } | null;
        largeSizeAdjustmentPercent: number | null;
        atOrAboveImplied: boolean;
      }>;
      valuationDate: string;
      recencySkewsOld: boolean;
      ratio: {
        value: number;
        fractional: boolean;
      };
      strongestComps: {
        count: number;
        sqftLow: number | null;
        sqftHigh: number | null;
        priceLow: Money<"market"> | null;
        priceHigh: Money<"market"> | null;
      } | null;
      contradicting: {
        address: string;
        adjustedValue: Money<"market">;
        others: number;
        compsLead: boolean;
      } | null;
      assessedLooksTooLow: boolean;
      largeGap: {
        reductionPercent: number;
        compSqftLow: number | null;
        compSqftHigh: number | null;
        subjectSqft: number | null;
        sizeMismatch: boolean;
      } | null;
      hero: {
        kind: "acquisitionNoCase" | "noSuggestionCounted" | "noSuggestion" | "suggestion";
        supporting: number;
        usable: number;
      };
    };
    ask: {
      requestedValue: Money<"market"> | null;
      source: AskSource | null;
      estimatedReduction: {
        amount: Money<"assessed">;
        percent: number;
      } | null;
    };
    condition: {
      deduction: Money<"market"> | null;
      items: Array<{
        label: string;
        amount: Money<"market">;
        method: string;
        category: string;
        confidence: string;
        basis: string;
      }>;
      adjustedValue: Money<"market"> | null;
      adjustedAssessed: Money<"assessed"> | null;
      capApplied: boolean;
      source: "v1.conditionValue" | "none";
      reason: "noConditionEvidence" | "noAnchor" | "modelUnavailable" | "modelFailed" | "rateLimited" | "notValued" | null;
      note: string;
    };
    photos: Array<{
      photoId: string;
      caption: string;
      captionAuthor: "owner" | "drafted";
      isMaterialEvidence: boolean | null;
      captionDraft: "written" | "notUsefulEvidence" | "modelUnavailable" | "modelFailed" | "rateLimited" | null;
    }>;
    grounds: {
      reasons: Array<"conditionIssues" | "factualError" | "uniformity" | "incomeApproach" | "overvaluation" | "comparableSales">;
      recordErrorNote: string;
    };
  };
};

export type PhotoResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    photo: {
      photoId: string;
      contentType: "image/jpeg" | "image/png";
      byteLength: number;
      base64: string;
    };
  };
};

export type AttestationAct = {
  variantSha256: string;
  clientTimestampMs: number;
};

export type GenerateLetterRequest =
  | {
    op: "queue";
    appealId: string;
    idempotencyKey: string;
    mode: "build" | "revise";
    tone: "formal" | "respectful" | "direct";
    owner: {
      name: string;
      mailingAddress: string;
      email?: string;
      phone?: string;
    };
    attestations: {
      adoptAsOwn: AttestationAct;
      canRaiseAck?: AttestationAct;
    };
    revise?: {
      ownerConcern: string;
    };
    appVersion: string;
  }
  | {
    op: "fetch";
    appealId: string;
    generationAt: number;
  }
  | {
    op: "pdf";
    appealId: string;
    generationAt: number;
    signature?: LetterSignature;
    letterhead?: Letterhead;
  }
  | {
    op: "deleteAppeal";
    appealId: string;
  }
  | {
    op: "deleteProperty";
    appealId: string;
  };

export type QueueLetterResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: {
    "C-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          unlocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "G-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          state: string | null;
          supported: boolean;
          recheckedAtGeneration: true;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          state: string | null;
          supported: boolean;
          recheckedAtGeneration: true;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          state: string | null;
          supported: boolean;
          recheckedAtGeneration: true;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          state: string | null;
          supported: boolean;
          recheckedAtGeneration: true;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-6":
      | {
        ran: true;
        outcome: "pass";
        value: {
          adoptAsOwnRequired: true;
          canRaiseAckRequired: boolean;
          recorded: Array<{
            kind: "adoptAsOwn" | "canRaiseAck";
            assentRecordId: string;
          }>;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          adoptAsOwnRequired: true;
          canRaiseAckRequired: boolean;
          recorded: Array<{
            kind: "adoptAsOwn" | "canRaiseAck";
            assentRecordId: string;
          }>;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          adoptAsOwnRequired: true;
          canRaiseAckRequired: boolean;
          recorded: Array<{
            kind: "adoptAsOwn" | "canRaiseAck";
            assentRecordId: string;
          }>;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          adoptAsOwnRequired: true;
          canRaiseAckRequired: boolean;
          recorded: Array<{
            kind: "adoptAsOwn" | "canRaiseAck";
            assentRecordId: string;
          }>;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "G-6":
      | {
        ran: true;
        outcome: "pass";
        value: {
          blocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          blocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          blocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          blocked: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "A-caseLooksWeak":
      | {
        ran: true;
        outcome: "pass";
        value: {
          fork: "none" | "thinData" | "noCase";
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          fork: "none" | "thinData" | "noCase";
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          fork: "none" | "thinData" | "noCase";
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          fork: "none" | "thinData" | "noCase";
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-15":
      | {
        ran: true;
        outcome: "pass";
        value: {
          isLargeGap: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          isLargeGap: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          isLargeGap: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          isLargeGap: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "NEW-17":
      | {
        ran: true;
        outcome: "pass";
        value: PerAppealCaps;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: PerAppealCaps;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: PerAppealCaps;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: PerAppealCaps;
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
  };
  degraded: Array<DegradedField>;
  body: {
    queued: {
      status: "queued";
      generationAt: number;
      jobId: string;
      pollAfterMs: 5000;
      idempotentReplay: boolean;
    };
  };
};

export type LetterFailureCode = "REQUESTED_VALUE_WOULD_RAISE" | "INSUFFICIENT_BASIS" | "NO_REDUCTION_CASE" | "BELOW_RECENT_PURCHASE" | "LETTER_VALIDATION_FAILED" | "LETTER_GENERATION_FAILED";

export type LetterJobPending = {
  generationAt: number;
  pollAfterMs: 5000;
  updatedAt: string;
  inFlight: boolean;
};

export type LetterJobView =
  | { status: "queued" } & LetterJobPending
  | { status: "generating" } & LetterJobPending
  | {
    status: "shipped";
    generationAt: number;
    letter: {
      text: string;
      summary: string;
      summaryMaxChars: 1000;
      auditFindings: Array<string>;
      exhibit: {
        conclusion: string | null;
      };
      concernResponse: {
        category: "prose" | "evidence" | "fact";
        message: string;
        applied: boolean;
        unresolved: boolean;
        needsRebuild: boolean;
      } | null;
    };
    artifacts: {
      pdf:
        | {
          available: true;
          filename: string;
        }
        | {
          available: false;
          reason: "letterFactsMissing";
          copy: Copy;
        };
      email: {
        available: boolean;
      };
    };
    letterSha256: string;
    review: LetterReview | null;
  }
  | {
    status: "failed";
    generationAt: number;
    failure: {
      httpStatus: number;
      code: LetterFailureCode;
      detail: { [key: string]: unknown };
      route: "value" | "comps" | "evidence" | "noCaseVerdict" | "retry" | "support";
      copy: Copy;
    };
  };

export type FetchLetterResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    job: LetterJobView;
  };
};

export type PdfLetterResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    pdf: {
      filename: string;
      contentType: "application/pdf";
      pageCount: number;
      byteLength: number;
      sha256: string;
      base64: string;
    };
  };
};

export type LetterSignature = {
  pngBase64: string;
};

export type Letterhead = {
  name: string;
  mailingAddress: string;
  city: string;
  state: string;
  zip: string;
  email: string;
  phone: string;
};

export type LetterReview = {
  paper: {
    reLine: string;
    filingTerm: "appeal" | "protest" | "petition";
    compValues: Array<Money<"market">>;
  };
  letterhead: {
    minimallyComplete: boolean;
  };
  stale: boolean;
  queuedWith: {
    mode: "build" | "revise";
    tone: "formal" | "respectful" | "direct";
    owner: {
      name: string;
      mailingAddress: string;
      email: string | null;
      phone: string | null;
    };
    attestations: {
      adoptAsOwn: AttestationAct | null;
      canRaiseAck: AttestationAct | null;
    };
  };
  concernReport: {
    title: Copy;
    message: string;
    outcomeLine: Copy | null;
    next: "takeMeThere" | "rebuild" | "ok";
  } | null;
  history: Array<{
    generationAt: number;
    filename: string;
  }>;
};

export type EmailLetterRequest = {
  appealId: string;
  generationAt: number;
  to: string;
  signature?: LetterSignature;
  letterhead?: Letterhead;
};

export type EmailLetterResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    sent: {
      to: string;
    };
  };
};

export type DeleteAppealResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    deleted: true;
  };
};

export type DeletePropertyResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    deleted: true;
    appealIds: Array<string>;
  };
};

export type ReturningOwnerOffer = {
  eligible: boolean;
  productId: string | null;
  available: number;
};

export type CreditsRequest =
  | {
    op: "get";
  }
  | {
    op: "spend";
    appealId: string;
    confirmations?: {
      address?: boolean;
    };
    shownCopy?: Array<string>;
    appVersion?: string;
  }
  | {
    op: "assent";
    kind: "onboardingTerms" | "reacceptance";
    termsSha256: string;
    privacySha256: string;
    termsVersion: number;
    privacyVersion: number;
    clientTimestampMs: number;
    appVersion: string;
    queuedOffline?: boolean;
    checkboxSha256?: string;
  }
  | {
    op: "deleteAccount";
    shownWarnings?: Array<string>;
  }
  | {
    op: "sendVerification";
  }
  | {
    op: "sendPasswordReset";
    email: string;
  }
  | {
    op: "sendEmailChange";
    newEmail: string;
  }
  | {
    op: "sendPasswordChanged";
  }
  | {
    op: "contactGet";
  }
  | {
    op: "contactSet";
    surface: ContactSurface;
    clientTimestampMs: number;
    reminders?: ConsentAct;
    marketing?: ConsentAct;
  }
  | {
    op: "signOutEverywhere";
  };

export type CreditsGetResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    credits: number;
    offers: {
      returningOwner: ReturningOwnerOffer;
    };
    purchase: {
      apple: {
        productIds: Array<string>;
      };
      web: {
        available: false | true;
        reason: "W4" | null;
        pendingPayment: Copy | null;
        paymentFailed: Copy | null;
      };
    };
    legal: {
      accepted: unknown;
      consentOnFile: boolean;
      consentRequired?: true;
      terms: LegalDocRef;
      privacy: LegalDocRef;
      attestations: Array<AttestationCopy>;
      changeSummary: Array<PinnedCopy>;
      privacyChangeSummary: Array<PinnedCopy>;
    };
    caps: {
      generations: 8;
      revisions: 20;
      evaluations: 10;
      providerSearches: 1;
    };
    contract: {
      version: string;
      schemaSha256: string;
      minClientVersion: string;
    };
    capabilities: {
      webAccounts: boolean;
      purchaseNoticePrespend?: true;
      resolveResume?: true;
      freeCheckFirstLook?: true;
    };
  };
};

export type CreditsSpendResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: {
    "G-1":
      | {
        ran: true;
        outcome: "pass";
        value: {
          state: string | null;
          supported: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          state: string | null;
          supported: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          state: string | null;
          supported: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          state: string | null;
          supported: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "G-3":
      | {
        ran: true;
        outcome: "pass";
        value: {
          confirmationRequired: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          confirmationRequired: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          confirmationRequired: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          confirmationRequired: boolean;
          confirmed: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "G-4":
      | {
        ran: true;
        outcome: "pass";
        value: {
          unitRequired: boolean;
          unitMissing: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          unitRequired: boolean;
          unitMissing: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          unitRequired: boolean;
          unitMissing: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          unitRequired: boolean;
          unitMissing: boolean;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
    "G-5":
      | {
        ran: true;
        outcome: "pass";
        value: {
          refusal: PropertyRefusal | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy | null;
      }
      | {
        ran: true;
        outcome: "info";
        value: {
          refusal: PropertyRefusal | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "warn";
        value: {
          refusal: PropertyRefusal | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | {
        ran: true;
        outcome: "block";
        value: {
          refusal: PropertyRefusal | null;
        };
        evaluatedOver: { [key: string]: number };
        reason?: string;
        copy: Copy;
      }
      | { ran: false } & DecisionNotRun;
  };
  degraded: Array<DegradedField>;
  body: {
    spend: {
      ok: boolean;
      credits: number;
      alreadyApplied: boolean;
    };
  };
};

export type CreditsAssentResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    assentRecordId: string;
  };
};

export type CreditsDeleteAccountResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    deleted: true;
    signInDeleted: boolean;
  };
};

export type CreditsAccountEmailResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    accepted: true;
  };
};

export type ContactSurface = "web-account" | "web-settings" | "web-letter";

export type ConsentAct = {
  on: boolean;
  textSha256: string;
};

export type ContactState = {
  email: string | null;
  reminders: boolean;
  marketing: boolean;
  nextYearReminderOnly: boolean;
};

export type CreditsContactResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    contact: ContactState;
  };
};

export type CreditsSignOutEverywhereResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    signedOut: true;
  };
};

export type OutcomesRequest =
  | {
    op: "record";
    appealId: string;
    result: OutcomeResult;
    finalAssessedValue?: Money<"assessed">;
    sharedAnonymously: boolean;
    note?: string;
  }
  | {
    op: "list";
  }
  | {
    op: "stats";
    state?: string;
    fips?: string;
  };

export type AppealSummary = {
  appealId: string;
  propertyKey: string;
  year: number;
  askPosture: "reducesBill" | "wouldNotLowerBill" | null;
  estimatedReduction: {
    amount: Money<"assessed">;
    percent: number;
  } | null;
  projectedAnnualSaving: {
    annual: number | null;
    withheldBecause: "capState" | "unknownRate" | "notReduction" | "measure50" | null;
  };
  realizedReduction: Money<"assessed"> | null;
  realizedAnnualSaving: number | null;
  outcomeRate: {
    state: string;
    effectiveRate: number;
    ratio: number;
  } | null;
};

export type OutcomeStats = {
  available: boolean;
  scope: "county" | "state" | "national" | null;
  sampleSize: number;
  winRatePercent: number | null;
  medianReduction: number | null;
  medianAnnualSavings: number | null;
  totalAnnualSavings: number | null;
};

export type OutcomesResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    recorded?: {
      returningOwner: ReturningOwnerOffer;
      summary: AppealSummary;
    };
    appeals?: Array<AppealSnapshot>;
    summaries?: Array<AppealSummary>;
    stats?: OutcomeStats;
  };
};

export type CheckoutProduct = {
  productId: string;
  credits: number;
  unitAmountCents: number;
  currency: "usd";
  displayName: string;
  displayPrice: string;
  returningOwner: boolean;
};

export type CreateCheckoutRequest =
  | {
    op: "offers";
  }
  | {
    op: "create";
    productId: string;
  };

export type CreateCheckoutOffersResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    mode: "test" | "live";
    available: boolean;
    unavailable: Copy | null;
    products: Array<CheckoutProduct>;
    returningOwner: ReturningOwnerOffer;
  };
};

export type CreateCheckoutCreateResponse = {
  contract: ContractStamp;
  requestId: string;
  serverTime: string;
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: never };
  degraded: Array<DegradedField>;
  body: {
    redirectUrl: string;
  };
};

// ── Endpoint inputs and responses ───────────────────────────────────────────────────────────────────────────────

export type ResolvePropertyAddressInput = {
  appealId?: string;
  typedAddress: string;
  unit?: string | null;
  propertyType?: PropertyTypeCode | null;
  taxYear?: number;
  confirmations?: {
    address?: boolean;
  };
};

export type ResolvePropertyRecordInput = {
  appealId: string;
  ownerName?: string;
  freshLookup?: boolean;
  corrections?: {
    sqft?: number;
    beds?: number;
    baths?: number;
    assessedValue?: Money<"assessed">;
    marketValue?: Money<"market">;
    parcel?: string;
    propertyType?: PropertyTypeCode;
    yearBuilt?: number;
    lotSizeSqft?: number;
    lastSalePrice?: Money<"market">;
    lastSaleDate?: string;
    lastSaleDistressed?: true;
    ratioPercent?: number;
    taxYear?: number;
    county?: string;
    state?: string;
    appealDeadline?: string;
  };
  confirmations?: {
    record?: boolean;
  };
  unitChoice?: string;
  withoutLookup?: true;
};

export type ResolvePropertyPurchaseNoticeInput = {
  appealId: string;
};

export type ResolvePropertyResumeInput = {
  appealId: string;
};

export type ResolvePropertyFreeCheckInput = {
  appealId: string;
};

export type SearchCompsSearchInput = {
  appealId: string;
  radiusMiles?: number;
};

export type SearchCompsEditInput = {
  appealId: string;
  include?: Array<string>;
  exclude?: Array<string>;
  keepDespiteOutlier?: Array<string>;
  ownerComps?: Array<{
    address: string;
    salePrice: Money<"market">;
    saleDate?: string;
    sqft?: number;
    beds?: number;
    baths?: number;
    distanceMi?: number;
    unitCount?: number;
    yearBuilt?: number;
  }>;
  updates?: Array<{
    compId: string;
    address?: string;
    salePrice?: Money<"market"> | null;
    saleDate?: string | null;
    sqft?: number | null;
    beds?: number | null;
    baths?: number | null;
    distanceMi?: number | null;
    unitCount?: number | null;
    yearBuilt?: number | null;
  }>;
  remove?: Array<string>;
};

export type SearchCompsLookupInput = {
  appealId: string;
  address: string;
};

export type ValueComputeInput = {
  appealId: string;
};

export type ValueSetAskInput = {
  appealId: string;
  requestedValue: Money<"market"> | null;
  source: AskSource;
};

export type ValueEvidenceInput = {
  appealId: string;
  reasons?: Array<"conditionIssues" | "factualError" | "uniformity" | "incomeApproach" | "overvaluation" | "comparableSales">;
  conditionNote?: string;
  purchaseContextNote?: string;
  recordErrorNote?: string;
  photos?: Array<{
    photoId?: string;
    base64?: string;
    contentType?: "image/jpeg" | "image/png";
    caption?: string;
    captionAuthor?: "owner" | "drafted";
  }>;
  largeGap?: {
    confirmed: boolean;
    reasons: Array<string>;
  };
  uniformity?: {
    adopt: boolean;
  };
};

export type ValuePhotoInput = {
  appealId: string;
  photoId: string;
};

export type GenerateLetterQueueInput = {
  appealId: string;
  idempotencyKey: string;
  mode: "build" | "revise";
  tone: "formal" | "respectful" | "direct";
  owner: {
    name: string;
    mailingAddress: string;
    email?: string;
    phone?: string;
  };
  attestations: {
    adoptAsOwn: AttestationAct;
    canRaiseAck?: AttestationAct;
  };
  revise?: {
    ownerConcern: string;
  };
  appVersion: string;
};

export type GenerateLetterFetchInput = {
  appealId: string;
  generationAt: number;
};

export type GenerateLetterPdfInput = {
  appealId: string;
  generationAt: number;
  signature?: LetterSignature;
  letterhead?: Letterhead;
};

export type GenerateLetterDeleteAppealInput = {
  appealId: string;
};

export type GenerateLetterDeletePropertyInput = {
  appealId: string;
};

export type CreditsSpendInput = {
  appealId: string;
  confirmations?: {
    address?: boolean;
  };
  shownCopy?: Array<string>;
  appVersion?: string;
};

export type CreditsAssentInput = {
  kind: "onboardingTerms" | "reacceptance";
  termsSha256: string;
  privacySha256: string;
  termsVersion: number;
  privacyVersion: number;
  clientTimestampMs: number;
  appVersion: string;
  queuedOffline?: boolean;
  checkboxSha256?: string;
};

export type CreditsDeleteAccountInput = {
  shownWarnings?: Array<string>;
};

export type CreditsSendPasswordResetInput = {
  email: string;
};

export type CreditsSendEmailChangeInput = {
  newEmail: string;
};

export type CreditsContactSetInput = {
  surface: ContactSurface;
  clientTimestampMs: number;
  reminders?: ConsentAct;
  marketing?: ConsentAct;
};

export type OutcomesRecordInput = {
  appealId: string;
  result: OutcomeResult;
  finalAssessedValue?: Money<"assessed">;
  sharedAnonymously: boolean;
  note?: string;
};

export type OutcomesStatsInput = {
  state?: string;
  fips?: string;
};

export type CreateCheckoutCreateInput = {
  productId: string;
};

// ── The client ──────────────────────────────────────────────────────────────────────────────────────────────────

export type V2Endpoint = "v2ResolveProperty" | "v2SearchComps" | "v2EvaluateCase" | "v2Value" | "v2GenerateLetter" | "v2EmailLetter" | "v2Credits" | "v2Outcomes" | "v2CreateCheckout";

export type V2ContractStamp = { version: string; schemaSha256: string };

/** What contractCompatibility decides about a response's contract stamp. */
export type V2ContractVerdict =
  | "compatible" | "unparseableVersion" | "majorMismatch" | "olderMinor" | "schemaChangedWithoutBump";

export interface FetchResponseLike {
  readonly status: number;
  text(): Promise<string>;
}

/** The subset of `fetch` the client calls; `globalThis.fetch` satisfies it. */
export type FetchLike = (
  url: string,
  init: { method: "POST"; headers: { [name: string]: string }; body: string },
) => Promise<FetchResponseLike>;

export interface V2ClientOptions {
  /** An endpoint's URL. A string is a base, and the endpoint's name is appended after a slash. */
  readonly url: string | ((endpoint: V2Endpoint) => string);
  /** The signed-in user's Firebase Auth ID token. */
  readonly getIdToken: () => string | Promise<string>;
  /** A Firebase App Check token. */
  readonly getAppCheckToken: () => string | Promise<string>;
  /** Defaults to `globalThis.fetch`. */
  readonly fetch?: FetchLike;
}

export type V2Failure =
  | { readonly ok: false; readonly kind: "v2Error"; readonly status: number; readonly error: V2Error }
  | {
    readonly ok: false;
    readonly kind: "contractMismatch";
    readonly reason: Exclude<V2ContractVerdict, "compatible">;
    readonly status: number;
    readonly expected: V2ContractStamp;
    readonly received: V2ContractStamp;
    /** The parsed body, untyped: it was written against a contract this client cannot read. */
    readonly body: unknown;
  }
  | { readonly ok: false; readonly kind: "badResponse"; readonly status: number; readonly problem: string }
  | { readonly ok: false; readonly kind: "badRequest"; readonly problem: string }
  | { readonly ok: false; readonly kind: "network"; readonly problem: string }
  | { readonly ok: false; readonly kind: "token"; readonly which: "idToken" | "appCheck"; readonly problem: string };

export type V2Result<T> = { readonly ok: true; readonly status: number; readonly response: T } | V2Failure;

function isRecord(value: unknown): value is { readonly [key: string]: unknown } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** MAJOR.MINOR.PATCH with no leading zeros, or null: the parse the server's negotiation makes. */
function parseContractVersion(text: string): { major: number; minor: number; patch: number } | null {
  const match = /^(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)$/.exec(text);
  if (match === null) return null;
  const [major, minor, patch] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (!Number.isSafeInteger(major) || !Number.isSafeInteger(minor) || !Number.isSafeInteger(patch)) return null;
  return { major, minor, patch };
}

/**
 * Whether a client rendered at `expected` can read a response stamped `received`. It cannot when the server's version
 * does not parse, has a different MAJOR, or has an older MINOR, or when it is the client's own version with a different
 * schemaSha256 (the schema changed without a bump). Any other version is compatible whatever its hash: within a MAJOR, a
 * newer MINOR only adds.
 */
export function contractCompatibility(expected: V2ContractStamp, received: V2ContractStamp): V2ContractVerdict {
  const ours = parseContractVersion(expected.version);
  const theirs = parseContractVersion(received.version);
  if (ours === null || theirs === null) return "unparseableVersion";
  if (theirs.major !== ours.major) return "majorMismatch";
  if (theirs.minor < ours.minor) return "olderMinor";
  const sameVersion = theirs.minor === ours.minor && theirs.patch === ours.patch;
  if (sameVersion && received.schemaSha256 !== expected.schemaSha256) return "schemaChangedWithoutBump";
  return "compatible";
}

async function send<T>(options: V2ClientOptions, endpoint: V2Endpoint, body: object): Promise<V2Result<T>> {
  let idToken: string;
  try {
    idToken = await options.getIdToken();
  } catch (error) {
    return { ok: false, kind: "token", which: "idToken", problem: describeError(error) };
  }
  let appCheckToken: string;
  try {
    appCheckToken = await options.getAppCheckToken();
  } catch (error) {
    return { ok: false, kind: "token", which: "appCheck", problem: describeError(error) };
  }
  let payload: string;
  try {
    payload = JSON.stringify(body);
  } catch (error) {
    return { ok: false, kind: "badRequest", problem: describeError(error) };
  }
  const doFetch = options.fetch ?? (globalThis as unknown as { fetch?: FetchLike }).fetch;
  if (doFetch === undefined) return { ok: false, kind: "network", problem: "no fetch implementation is available" };
  let url: string;
  try {
    url = typeof options.url === "function" ? options.url(endpoint) : `${options.url.replace(/\/+$/, "")}/${endpoint}`;
  } catch (error) {
    return { ok: false, kind: "badRequest", problem: describeError(error) };
  }
  let status: number;
  let text: string;
  try {
    const response = await doFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
        "X-Firebase-AppCheck": appCheckToken,
        "x-proppeal-contract": CONTRACT_VERSION,
      },
      body: payload,
    });
    status = response.status;
    text = await response.text();
  } catch (error) {
    return { ok: false, kind: "network", problem: describeError(error) };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, kind: "badResponse", status, problem: "the body is not JSON" };
  }
  if (!isRecord(parsed) || !isRecord(parsed.contract)
    || typeof parsed.contract.version !== "string" || typeof parsed.contract.schemaSha256 !== "string") {
    return { ok: false, kind: "badResponse", status, problem: "the body carries no contract stamp" };
  }
  const received: V2ContractStamp = { version: parsed.contract.version, schemaSha256: parsed.contract.schemaSha256 };
  const expected: V2ContractStamp = { version: CONTRACT_VERSION, schemaSha256: SCHEMA_SHA256 };
  const verdict = contractCompatibility(expected, received);
  if (verdict !== "compatible") {
    return { ok: false, kind: "contractMismatch", reason: verdict, status, expected, received, body: parsed };
  }
  if (status >= 200 && status < 300) return { ok: true, status, response: parsed as T };
  if (isRecord(parsed.error)) return { ok: false, kind: "v2Error", status, error: parsed as V2Error };
  return { ok: false, kind: "badResponse", status, problem: "a non-2xx body carries no error" };
}

export function createV2Client(options: V2ClientOptions) {
  return {
  v2ResolveProperty: {
    address: (input: ResolvePropertyAddressInput): Promise<V2Result<ResolvePropertyResponse>> =>
      send<ResolvePropertyResponse>(options, "v2ResolveProperty", { ...input, op: "address" }),
    record: (input: ResolvePropertyRecordInput): Promise<V2Result<ResolvePropertyResponse>> =>
      send<ResolvePropertyResponse>(options, "v2ResolveProperty", { ...input, op: "record" }),
    purchaseNotice: (input: ResolvePropertyPurchaseNoticeInput): Promise<V2Result<ResolvePurchaseNoticeResponse>> =>
      send<ResolvePurchaseNoticeResponse>(options, "v2ResolveProperty", { ...input, op: "purchaseNotice" }),
    resume: (input: ResolvePropertyResumeInput): Promise<V2Result<ResolvePropertyResponse>> =>
      send<ResolvePropertyResponse>(options, "v2ResolveProperty", { ...input, op: "resume" }),
    freeCheck: (input: ResolvePropertyFreeCheckInput): Promise<V2Result<ResolveFreeCheckResponse>> =>
      send<ResolveFreeCheckResponse>(options, "v2ResolveProperty", { ...input, op: "freeCheck" }),
  },
  v2SearchComps: {
    search: (input: SearchCompsSearchInput): Promise<V2Result<SearchCompsResponse>> =>
      send<SearchCompsResponse>(options, "v2SearchComps", { ...input, op: "search" }),
    edit: (input: SearchCompsEditInput): Promise<V2Result<SearchCompsResponse>> =>
      send<SearchCompsResponse>(options, "v2SearchComps", { ...input, op: "edit" }),
    lookup: (input: SearchCompsLookupInput): Promise<V2Result<CompLookupResponse>> =>
      send<CompLookupResponse>(options, "v2SearchComps", { ...input, op: "lookup" }),
  },
  v2EvaluateCase: (input: EvaluateCaseRequest): Promise<V2Result<EvaluateCaseResponse>> =>
    send<EvaluateCaseResponse>(options, "v2EvaluateCase", input),
  v2Value: {
    compute: (input: ValueComputeInput): Promise<V2Result<ValueResponse>> =>
      send<ValueResponse>(options, "v2Value", { ...input, op: "compute" }),
    setAsk: (input: ValueSetAskInput): Promise<V2Result<ValueResponse>> =>
      send<ValueResponse>(options, "v2Value", { ...input, op: "setAsk" }),
    evidence: (input: ValueEvidenceInput): Promise<V2Result<ValueResponse>> =>
      send<ValueResponse>(options, "v2Value", { ...input, op: "evidence" }),
    photo: (input: ValuePhotoInput): Promise<V2Result<PhotoResponse>> =>
      send<PhotoResponse>(options, "v2Value", { ...input, op: "photo" }),
  },
  v2GenerateLetter: {
    queue: (input: GenerateLetterQueueInput): Promise<V2Result<QueueLetterResponse>> =>
      send<QueueLetterResponse>(options, "v2GenerateLetter", { ...input, op: "queue" }),
    fetch: (input: GenerateLetterFetchInput): Promise<V2Result<FetchLetterResponse>> =>
      send<FetchLetterResponse>(options, "v2GenerateLetter", { ...input, op: "fetch" }),
    pdf: (input: GenerateLetterPdfInput): Promise<V2Result<PdfLetterResponse>> =>
      send<PdfLetterResponse>(options, "v2GenerateLetter", { ...input, op: "pdf" }),
    deleteAppeal: (input: GenerateLetterDeleteAppealInput): Promise<V2Result<DeleteAppealResponse>> =>
      send<DeleteAppealResponse>(options, "v2GenerateLetter", { ...input, op: "deleteAppeal" }),
    deleteProperty: (input: GenerateLetterDeletePropertyInput): Promise<V2Result<DeletePropertyResponse>> =>
      send<DeletePropertyResponse>(options, "v2GenerateLetter", { ...input, op: "deleteProperty" }),
  },
  v2EmailLetter: (input: EmailLetterRequest): Promise<V2Result<EmailLetterResponse>> =>
    send<EmailLetterResponse>(options, "v2EmailLetter", input),
  v2Credits: {
    get: (): Promise<V2Result<CreditsGetResponse>> =>
      send<CreditsGetResponse>(options, "v2Credits", { op: "get" }),
    spend: (input: CreditsSpendInput): Promise<V2Result<CreditsSpendResponse>> =>
      send<CreditsSpendResponse>(options, "v2Credits", { ...input, op: "spend" }),
    assent: (input: CreditsAssentInput): Promise<V2Result<CreditsAssentResponse>> =>
      send<CreditsAssentResponse>(options, "v2Credits", { ...input, op: "assent" }),
    deleteAccount: (input: CreditsDeleteAccountInput): Promise<V2Result<CreditsDeleteAccountResponse>> =>
      send<CreditsDeleteAccountResponse>(options, "v2Credits", { ...input, op: "deleteAccount" }),
    sendVerification: (): Promise<V2Result<CreditsAccountEmailResponse>> =>
      send<CreditsAccountEmailResponse>(options, "v2Credits", { op: "sendVerification" }),
    sendPasswordReset: (input: CreditsSendPasswordResetInput): Promise<V2Result<CreditsAccountEmailResponse>> =>
      send<CreditsAccountEmailResponse>(options, "v2Credits", { ...input, op: "sendPasswordReset" }),
    sendEmailChange: (input: CreditsSendEmailChangeInput): Promise<V2Result<CreditsAccountEmailResponse>> =>
      send<CreditsAccountEmailResponse>(options, "v2Credits", { ...input, op: "sendEmailChange" }),
    sendPasswordChanged: (): Promise<V2Result<CreditsAccountEmailResponse>> =>
      send<CreditsAccountEmailResponse>(options, "v2Credits", { op: "sendPasswordChanged" }),
    contactGet: (): Promise<V2Result<CreditsContactResponse>> =>
      send<CreditsContactResponse>(options, "v2Credits", { op: "contactGet" }),
    contactSet: (input: CreditsContactSetInput): Promise<V2Result<CreditsContactResponse>> =>
      send<CreditsContactResponse>(options, "v2Credits", { ...input, op: "contactSet" }),
    signOutEverywhere: (): Promise<V2Result<CreditsSignOutEverywhereResponse>> =>
      send<CreditsSignOutEverywhereResponse>(options, "v2Credits", { op: "signOutEverywhere" }),
  },
  v2Outcomes: {
    record: (input: OutcomesRecordInput): Promise<V2Result<OutcomesResponse>> =>
      send<OutcomesResponse>(options, "v2Outcomes", { ...input, op: "record" }),
    list: (): Promise<V2Result<OutcomesResponse>> =>
      send<OutcomesResponse>(options, "v2Outcomes", { op: "list" }),
    stats: (input: OutcomesStatsInput): Promise<V2Result<OutcomesResponse>> =>
      send<OutcomesResponse>(options, "v2Outcomes", { ...input, op: "stats" }),
  },
  v2CreateCheckout: {
    offers: (): Promise<V2Result<CreateCheckoutOffersResponse>> =>
      send<CreateCheckoutOffersResponse>(options, "v2CreateCheckout", { op: "offers" }),
    create: (input: CreateCheckoutCreateInput): Promise<V2Result<CreateCheckoutCreateResponse>> =>
      send<CreateCheckoutCreateResponse>(options, "v2CreateCheckout", { ...input, op: "create" }),
  },
  };
}

export type V2Client = ReturnType<typeof createV2Client>;
